import React, { Component } from 'react';
import {Link} from 'react-router-dom';

import HttpRunner from '../query';
import EntityIdQuery from '../entityIdQuery';
import urlHandler from '../../../utils/urlHandler';
import miscUtils from '../../../utils/miscUtils';

class DocCustomEntities extends Component {
    constructor(props){
        super(props);
    }

    render() {
        return (
            <div className="bucket" id="top">
                <h3 className="heading-1">Custom Entity Fields</h3>
                <div className="description">
                The Custom Entity Web API uses a very specific schema with the importance that we use the same validation, normalization, and tagging. This ensures all properties are executed appropriately.
                <br/>&nbsp;<br/>
                <div className="note">
                        <span className="fa fa-info-circle">&nbsp;</span>
                        When handling bulk data, it is recommended to call /insert, /delete, /validate with larger lists of entries in a single request rather than one entry at a time.
                    </div>
                </div>
                <br/>
                Below is an overview of the general schema required for any and all of the following extraction endpoints:<br/>

                <Link to="/documentation/doc_v1extractioncreate#top">POST - /api/v1/extraction/insert</Link><br/>
                <Link to="/documentation/doc_v1extractionvalidate#top">POST - /api/v1/extraction/validate</Link><br/>
                <Link to="/documentation/doc_v1extractiondelete#top">DELETE - /api/v1/extraction/delete</Link>
                <br/>
                <h4 className="heading-2">Custom Entity</h4>

                <h4 className="heading-3" id="custom_entity_object">Custom Entity Object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Values </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">entries</td>
                        <td className="meaning">A list of JSON entry objects to ADD or REMOVE tags from the output.</td>
                        <td className="type">entry[]</td>
                        <td className="type">Required</td>
                    </tr>
                    </tbody>
                </table>
                <br/>
                <h4 className="heading-2" id="custom_entity_entry_object">Entries</h4>
                <h4 className="heading-3">Entry Object</h4>
                The entries JSON Objects array also has a very specific schema. Here are the required elements for specifying a particular entry in the list and whether the intention is add or remove it to/from document tagging.
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Values </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">matchText</td>
                        <td className="meaning">The string to match.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">entityType</td>
                        <td className="meaning">The entity type of the match. (This is required for both ADD and REMOVE. To remove a matchText, the entityType must match the original extraction type exactly.)
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">action</td>
                        <td className="meaning">Must be specified as "ADD" or "REMOVE".
                            <br/>&nbsp;<br/>
                            Adding a PERSON entry requires additional metadata. See below.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">exactMatch</td>
                        <td className="meaning">If true, the extraction must match the specified matchText exactly. Default is false and this object is ignored if the value if anything other than true.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">matchCase</td>
                        <td className="meaning">If true, the extraction must match the case of specified matchText. Default is false and this object is ignored if the value if anything other than true.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">matchPunctuation</td>
                        <td className="meaning">If true, the extraction must match the punctuation of specified matchText. Default is false and this object is ignored if the value if anything other than true.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">label</td>
                        <td className="meaning">The label value for the match. This can be used for normalizing non-disambiguated entities or to improve the disambiguation of an entity.</td>
                        <td className="type">String</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">unrelated</td>
                        <td className="meaning">If true, the extractions of the matchText will all be marked as unrelated. Default is false and this object is ignored if the value if anything other than true.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">entitySubString</td>
                        <td className="meaning">If the provided matchText is found, the string will be adjusted to what is provided in the entitySubString object. This substring is case insensitive and MUST be contained within the original matchText. Useful if you may want to match a problematic phrase but return only a valid portion of it.</td>
                        <td className="type">String</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">removeAllOverlap</td>
                        <td className="meaning">If true, allows a particular matchText to remove any overlapping entity that is extracted by Finch for Text.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">ignoreSubType</td>
                        <td className="meaning">Allows a dictionary entry to remove a matchText matching any entity subtype. This differs from the default behavior where the entity type MUST be an exact match when suppressing.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">disambiguate</td>
                        <td className="meaning">Ensures such tags are never disambiguated when extracted. This value can only be false. By default, all custom entity tags will atleast attempt to be disambiguated.</td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">metadata</td>
                        <td className="meaning">Pass through JsonElements to be represented as attributes along side the matchText.
                            <br/>&nbsp;<br/>
                            For PERSON entries, one or more of the following elements should be utilized:<br/>
                            <ul style={{marginLeft:'10%'}}>
                                <li>firstName</li>
                                <li>lastName</li>
                                <li>middleName</li>
                            </ul>
                        </td>
                        <td className="type">JsonObject</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">languages</td>
                        <td className="meaning">Ensures a matchText is only matched 
                            for a certain set of languages. This can be helpful if 
                            you want a tag that has a different meaning in another 
                            language or this can be used to remove a entity for only 
                            a specific set of languages. 
                        </td>
                        <td className="type">String[]</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    </tbody>
                </table>

            </div>
        );
    }
}

export default DocCustomEntities;